import { debounce } from 'lodash';
import { useCallback, useMemo } from 'react';
import useLocalStorageState from 'use-local-storage-state';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import {
  DEFAULT_EDITOR_SETTINGS,
  IEditorSettings,
} from 'components/DevSandbox/types';

import { useSupabaseTypedClient } from './reactQuery';

const EDITOR_SETTINGS_STORAGE_KEY = 'bds-editor-settings';

interface IUseEditorSettingsOptions {
  /** Author's editor settings override (for solution viewing) */
  authorOverride?: Partial<IEditorSettings> | null;
}

interface IUseEditorSettingsReturn {
  settings: IEditorSettings;
  updateSettings: (partial: Partial<IEditorSettings>) => void;
  resetSettings: () => void;
}

/**
 * Hook for managing editor settings.
 * - Authenticated users: persists to database (debounced)
 * - Demo/unauthenticated: uses localStorage
 * - Always merges with DEFAULT_EDITOR_SETTINGS for missing fields
 * - When authorOverride is provided, framework/cssMode/packages use author's values
 */
export const useEditorSettings = (options?: IUseEditorSettingsOptions): IUseEditorSettingsReturn => {
  const currentUser = useCurrentUser();
  const supabase = useSupabaseTypedClient();
  const userId = currentUser?.id;
  const isAuthenticated = Boolean(userId);

  // localStorage for both authenticated and unauthenticated (as cache/fallback)
  const [localSettings, setLocalSettings] = useLocalStorageState<Partial<IEditorSettings>>(
    EDITOR_SETTINGS_STORAGE_KEY,
    { defaultValue: {} }
  );

  const authorOverride = options?.authorOverride;

  // Merge with defaults to ensure all fields exist
  // When authorOverride is provided, use author's framework/cssMode/packages for correct rendering
  const settings: IEditorSettings = useMemo(() => {
    const base = {
      ...DEFAULT_EDITOR_SETTINGS,
      ...localSettings,
    };

    if (authorOverride) {
      return {
        ...base,
        ...(authorOverride.framework && { framework: authorOverride.framework as IEditorSettings['framework'] }),
        ...(authorOverride.cssMode && { cssMode: authorOverride.cssMode as IEditorSettings['cssMode'] }),
        ...(authorOverride.packages && { packages: authorOverride.packages as IEditorSettings['packages'] }),
      };
    }

    return base;
  }, [localSettings, authorOverride]);

  // Debounced DB update for authenticated users
  const debouncedDbUpdate = useMemo(
    () =>
      debounce(async (newSettings: Partial<IEditorSettings>) => {
        if (!userId) return;

        try {
          // Note: editor_settings column needs to be added via migration
          // Cast to any until the schema is updated
          await supabase
            .from('user')
            .update({ editor_settings: newSettings } as any)
            .eq('id', userId);
        } catch (error) {
          console.error('Failed to save editor settings to database:', error);
        }
      }, 500),
    [userId, supabase]
  );

  const updateSettings = useCallback(
    (partial: Partial<IEditorSettings>) => {
      const newSettings = {
        ...localSettings,
        ...partial,
      };

      // Always update localStorage (for immediate feedback and as cache)
      setLocalSettings(newSettings);

      // If authenticated, also persist to database
      if (isAuthenticated) {
        debouncedDbUpdate(newSettings);
      }
    },
    [localSettings, setLocalSettings, isAuthenticated, debouncedDbUpdate]
  );

  const resetSettings = useCallback(() => {
    setLocalSettings({});

    if (isAuthenticated) {
      debouncedDbUpdate({});
    }
  }, [setLocalSettings, isAuthenticated, debouncedDbUpdate]);

  return {
    settings,
    updateSettings,
    resetSettings,
  };
};
